import { ArrowRight, MapPin, MessageSquareText, PhoneCall } from "lucide-react";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";

const heroStats = [
  { label: "Serving Chicago since", value: "2019", slug: "years" },
  { label: "Google review average", value: "5.0", slug: "rating" },
  { label: "Published reviews", value: "124+", slug: "reviews" },
];

const heroImage = "https://customer-assets.emergentagent.com/job_d-detail-pro/artifacts/aluymmvv_IMG_1048.jpeg";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 95, damping: 18 } },
};

export const HeroSection = ({ bookingUrl, trustBadges, logoUrl }) => {
  return (
    <section className="relative isolate min-h-screen overflow-hidden border-b border-white/10" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        data-testid="hero-background-image"
        style={{ backgroundImage: `url(${heroImage})` }}
      />
      <div className="absolute inset-0 bg-black/62" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_48%,rgba(96,165,250,0.14),transparent_24%),linear-gradient(to_bottom,rgba(5,5,5,0.12),rgba(5,5,5,0.62)_58%,#050505_100%)]" />
      <div className="grain-overlay" />
      <div className="hero-lens-flare" />

      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-4 pb-16 pt-6 sm:px-6 lg:px-8 lg:pb-20 lg:pt-8">
        <motion.div
          animate="visible"
          className="glass-chip luxury-outline mt-3 flex items-center justify-between gap-6 px-5 py-4"
          data-testid="top-navigation-bar"
          initial="hidden"
          variants={reveal}
        >
          <div className="space-y-1" data-testid="brand-lockup">
            <div className="flex items-center gap-4" data-testid="brand-logo-row">
              <div className="logo-full-shell h-14 w-[132px] sm:h-16 sm:w-[160px]" data-testid="header-logo-mark-shell">
                <img
                  alt="Detail With D full logo"
                  className="logo-full-image"
                  data-testid="header-logo-mark-image"
                  fetchPriority="high"
                  src={logoUrl}
                />
              </div>

              <div>
                <p className="section-kicker text-[0.7rem] text-zinc-400" data-testid="brand-kicker">
                  Chicago window tint specialist
                </p>
                <p className="text-sm font-medium tracking-[0.16em] text-white sm:text-base" data-testid="brand-subtitle">
                  Ceramic · Carbon · IR Heat Rejection
                </p>
              </div>
            </div>
          </div>

          <div className="hidden items-center gap-3 md:flex" data-testid="desktop-navigation-actions">
            <a className="text-sm font-medium text-zinc-300 transition-colors duration-300 hover:text-white" data-testid="nav-phone-link" href="tel:+17737040795">
              (773) 704-0795
            </a>
            <Button asChild className="glass-button glass-button-secondary h-11 px-5 text-white transition-colors duration-300 hover:bg-white/10" variant="ghost">
              <a data-testid="nav-estimate-link" href="#contact">
                Free estimate
              </a>
            </Button>
            <Button asChild className="glass-button glass-button-primary h-11 px-5 text-white transition-colors duration-300 hover:bg-blue-500">
              <a data-testid="nav-book-now-link" href={bookingUrl} rel="noreferrer" target="_blank">
                Book now
              </a>
            </Button>
          </div>
        </motion.div>

        <div className="hero-grid flex-1 items-end py-12 lg:py-20">
          <motion.div
            className="col-span-12 flex flex-col justify-end gap-8 lg:col-span-8 lg:pr-8"
            data-testid="hero-copy-column"
            initial="hidden"
            transition={{ delayChildren: 0.12, staggerChildren: 0.1 }}
            viewport={{ once: true, amount: 0.2 }}
            whileInView="visible"
          >
            <div className="space-y-6">
              <motion.div className="glass-chip inline-flex w-fit items-center gap-2 px-4 py-2 text-xs tracking-[0.26em] text-zinc-200" data-testid="hero-badge" variants={reveal}>
                <MapPin className="h-4 w-4 text-blue-400" />
                Premium window tint in Chicago, IL
              </motion.div>

              <motion.div className="max-w-4xl space-y-5" variants={reveal}>
                <div className="hero-logo-full-shell mb-6 w-[230px] sm:w-[280px]" data-testid="hero-logo-mark-shell">
                  <img
                    alt="Detail With D full brand logo"
                    className="hero-logo-full-image"
                    data-testid="hero-logo-mark-image"
                    src={logoUrl}
                  />
                </div>

                <h1 className="max-w-5xl text-[clamp(3.4rem,8vw,6.2rem)] font-light leading-[0.92] text-white" data-testid="hero-heading">
                  Premium tint for cars that deserve a <span className="metallic-text">cooler cabin</span> and a cleaner look.
                </h1>
                <p className="max-w-2xl text-base leading-8 text-zinc-200 sm:text-lg" data-testid="hero-description">
                  Detail With D specializes in window tint installation that helps reduce heat, block glare, protect interiors, and give your vehicle a sharper finished profile the moment you drive away.
                </p>
              </motion.div>
            </div>

            <motion.div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap" data-testid="hero-primary-actions" variants={reveal}>
              <Button asChild className="glass-button glass-button-primary h-14 px-7 text-sm tracking-[0.18em] text-white transition-colors duration-300 hover:bg-blue-500">
                <a data-testid="hero-book-now-link" href={bookingUrl} rel="noreferrer" target="_blank">
                  Book your appointment
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild className="glass-button glass-button-secondary h-14 px-7 text-sm tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white/10" variant="ghost">
                <a data-testid="hero-get-estimate-link" href="#contact">
                  Get a free estimate
                </a>
              </Button>
            </motion.div>

            <motion.div className="grid gap-4 sm:grid-cols-3" data-testid="hero-stats-grid" variants={reveal}>
              {heroStats.map((stat) => (
                <motion.div
                  key={stat.slug}
                  className="floating-stack panel-surface overflow-hidden p-5"
                  data-testid={`hero-stat-card-${stat.slug}`}
                  transition={{ type: "spring", stiffness: 110, damping: 18 }}
                  whileHover={{ y: -8, scale: 1.01 }}
                >
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-500" data-testid={`hero-stat-label-${stat.slug}`}>
                    {stat.label}
                  </p>
                  <p className="mt-3 text-[2.65rem] font-medium leading-none text-white" data-testid={`hero-stat-value-${stat.slug}`}>
                    {stat.value}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="col-span-12 grid content-end gap-4 lg:col-span-4"
            data-testid="hero-side-panel-column"
            initial="hidden"
            transition={{ delay: 0.18, type: "spring", stiffness: 90, damping: 18 }}
            viewport={{ once: true, amount: 0.2 }}
            variants={reveal}
            whileInView="visible"
          >
            <motion.div className="panel-surface space-y-5 p-6" data-testid="hero-side-panel" whileHover={{ y: -8 }}>
              <div className="space-y-3">
                <p className="section-kicker text-xs text-blue-400" data-testid="hero-side-panel-kicker">
                  Why drivers book
                </p>
                <p className="text-[1.8rem] font-medium leading-[1.02] text-white sm:text-[2.15rem]" data-testid="hero-side-panel-heading">
                  Cooler cabin. Cleaner look. No wasted time.
                </p>
                <p className="text-sm leading-7 text-zinc-200" data-testid="hero-side-panel-description">
                  Schedule your tint consultation and get a clean install that cuts heat, improves privacy, and makes the whole vehicle feel more refined.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1" data-testid="hero-contact-links">
                <a className="glass-chip flex items-center justify-between px-4 py-4 text-sm text-white transition-colors duration-300 hover:border-blue-500" data-testid="hero-call-link" href="tel:+17737040795">
                  <span className="flex items-center gap-3"><PhoneCall className="h-4 w-4 text-blue-400" /> Call</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a className="glass-chip flex items-center justify-between px-4 py-4 text-sm text-white transition-colors duration-300 hover:border-blue-500" data-testid="hero-text-link" href="sms:+17737040795">
                  <span className="flex items-center gap-3"><MessageSquareText className="h-4 w-4 text-blue-400" /> Text</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </motion.div>

            <div className="flex flex-wrap gap-2" data-testid="hero-trust-badges">
              {trustBadges.map((badge) => (
                <div
                  key={badge}
                  className="glass-chip px-3 py-2 text-xs tracking-[0.18em] text-zinc-200"
                  data-testid={`hero-trust-badge-${badge.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {badge}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};